/**
 * useDocumentJobStates — Pipeline v3 Job-Status fuer eine Liste von Dokumenten.
 *
 * Holt Batched ueber POST /api/documents/job-status den OCR-/Title-Stage-Status
 * pro Dok-ID. Polling alle 15s, aber NUR wenn mindestens ein Dokument im
 * pending_*-Status ist — sonst ein Single-Fetch ohne refetch.
 *
 * Returns: Map<docId, JobState>
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export type JobState = {
  document_id: number;
  state: 'pending_ocr' | 'pending_title' | 'failed_ocr' | 'failed_title' | 'done';
  ocr: { id: number; status: string; attempts: number; error: string | null } | null;
  title: { id: number; status: string; attempts: number; error: string | null } | null;
};

export type JobStateMap = Record<number, JobState>;

const POLL_INTERVAL_MS = 15_000;

async function fetchJobStates(ids: number[]): Promise<JobStateMap> {
  if (!ids.length) return {};
  const res = await fetch('/api/documents/job-status', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`job-status HTTP ${res.status}`);
  return res.json();
}

export function useDocumentJobStates(ids: number[]) {
  const queryClient = useQueryClient();
  // Stabilisierter Key (Reihenfolge ignorieren, damit Polling stabil bleibt)
  const sortedKey = [...ids].sort((a, b) => a - b);

  const query = useQuery({
    queryKey: ['document-jobs', sortedKey],
    queryFn: () => fetchJobStates(sortedKey),
    enabled: sortedKey.length > 0,
    staleTime: 5_000,
  });

  const map = (query.data || {}) as JobStateMap;

  // Bedingtes Polling: nur wenn irgendein Doc pending_* ist
  const hasPending = Object.values(map).some(
    (s) => s.state === 'pending_ocr' || s.state === 'pending_title',
  );

  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['document-jobs', sortedKey] });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPending, sortedKey.join(',')]);

  // Side-effect: wenn ein Title-Job fertig wird, aktualisiert der Worker den
  // documents.title — wir invalidieren also auch die Doc-Liste damit der neue
  // Titel sichtbar wird, sobald alle pending_* verschwinden.
  useEffect(() => {
    if (!query.data) return;
    if (!hasPending) {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    }
  }, [hasPending, query.data, queryClient]);

  return { jobStates: map, isLoading: query.isLoading, refetch: query.refetch };
}
